import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { getMeetingDashboardSocket } from '../../utils/meetingDashboardSocket';

const HIDDEN_ROUTE_PREFIXES = [
  '/room/',
  '/join-room',
  '/interview-join',
  '/meeting-join',
  '/left-meeting/',
  '/meeting-ended',
  '/rejoin/',
];

const formatDateTime = (value) => {
  if (!value) return 'Schedule not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Schedule not available';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getJoinCountdown = (joinOpensAt, canJoin) => {
  if (canJoin) return 'Join window is open';
  if (!joinOpensAt) return 'Waiting for the interview window';

  const opensAt = new Date(joinOpensAt).getTime();
  if (!Number.isFinite(opensAt)) return 'Waiting for the interview window';

  const diff = Math.max(0, opensAt - Date.now());
  const minutes = Math.ceil(diff / 60000);

  return minutes > 0
    ? `Join opens in ${minutes} minute${minutes === 1 ? '' : 's'}`
    : 'Refreshing join permission...';
};

const ActiveInterviews = () => {
  const location = useLocation();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState('');
  const [, forceClockTick] = useState(0);

  const hidden = useMemo(
    () => HIDDEN_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)),
    [location.pathname]
  );

  const loadInterviews = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const response = await api.get('/interviews/active', {
        params: { _ts: Date.now() },
      });

      const list = Array.isArray(response.data?.interviews)
        ? response.data.interviews
        : Array.isArray(response.data)
          ? response.data
          : [];

      setInterviews(list);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Active interview loading failed:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hidden) return undefined;

    void loadInterviews();

    const refreshTimer = window.setInterval(
      () => void loadInterviews({ silent: true }),
      30_000
    );

    const clockTimer = window.setInterval(
      () => forceClockTick((value) => value + 1),
      30_000
    );

    const socket = getMeetingDashboardSocket();
    const refreshFromSocket = () => void loadInterviews({ silent: true });

    socket?.on('interview-scheduled', refreshFromSocket);
    socket?.on('interview-updated', refreshFromSocket);
    socket?.on('meeting-ended', refreshFromSocket);

    return () => {
      window.clearInterval(refreshTimer);
      window.clearInterval(clockTimer);
      socket?.off('interview-scheduled', refreshFromSocket);
      socket?.off('interview-updated', refreshFromSocket);
      socket?.off('meeting-ended', refreshFromSocket);
    };
  }, [hidden, loadInterviews]);

  const joinInterview = async (item) => {
    if (!item?.roomId || joiningRoomId) return;

    setJoiningRoomId(item.roomId);

    try {
      const response = await api.get(
        `/interviews/${encodeURIComponent(item.roomId)}/personal-link`,
        { params: { _ts: Date.now() } }
      );

      const fresh = response.data || {};
      if (fresh.canJoin !== true) {
        toast.info(getJoinCountdown(fresh.joinOpensAt, fresh.canJoin));
        setInterviews((current) =>
          current.map((existing) =>
            existing.roomId === item.roomId ? { ...existing, ...fresh } : existing
          )
        );
        return;
      }

      const personalLink = fresh.personalLink || fresh.meetingLink;
      if (!personalLink) {
        throw new Error('Personal interview link was not returned.');
      }

      // Full-page navigation preserves the signed token and also works in
      // normal browsers, Android WebView, and Capacitor deep links.
      window.location.assign(personalLink);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Unable to open the interview.';

      toast.error(message);
      void loadInterviews({ silent: true });
    } finally {
      setJoiningRoomId('');
    }
  };

  if (hidden || (!loading && interviews.length === 0)) return null;

  return (
    <aside className="fixed bottom-5 right-5 z-[170] w-[min(92vw,390px)] space-y-3">
      {loading && interviews.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-slate-950/95 p-4 text-sm text-white shadow-2xl backdrop-blur-xl">
          Loading scheduled interviews...
        </div>
      ) : (
        interviews.slice(0, 4).map((item) => {
          const access = item.access || {};
          const participantStatus = item.participantStatus || 'waiting';
          const canJoin = item.canJoin === true;
          const joining = joiningRoomId === item.roomId;

          return (
            <article
              key={item._id || item.roomId}
              className="overflow-hidden rounded-2xl border border-white/10 bg-slate-950/95 text-white shadow-2xl backdrop-blur-xl"
            >
              <div className="border-b border-white/10 bg-gradient-to-r from-[#7f083c] to-[#a30b4d] px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-200">
                      Scheduled Interview
                    </p>
                    <h3 className="mt-0.5 line-clamp-1 text-base font-bold">
                      {item.applicantName || item.title || 'Admission Interview'}
                    </h3>
                  </div>
                  <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase">
                    {access.isHost ? 'Admin host' : participantStatus}
                  </span>
                </div>
              </div>

              <div className="space-y-3 px-4 py-4">
                <div>
                  <p className="text-xs text-slate-400">Date and time (IST)</p>
                  <p className="mt-0.5 text-sm font-semibold">
                    {formatDateTime(item.scheduledAt)}
                  </p>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-xl bg-white/5 px-3 py-2.5">
                  <div>
                    <p className="text-xs font-medium text-slate-200">
                      {getJoinCountdown(item.joinOpensAt, canJoin)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {access.isHost
                        ? 'You will enter as the fixed interview host.'
                        : 'You will enter the waiting room for admin admission.'}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={!canJoin || joining}
                    onClick={() => void joinInterview(item)}
                    className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
                  >
                    {joining ? 'Opening...' : canJoin ? 'Join' : 'Not open'}
                  </button>
                </div>
              </div>
            </article>
          );
        })
      )}
    </aside>
  );
};

export default ActiveInterviews;
